<script setup>
import { useI18n } from 'vue-i18n';

import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';

/**
 * OpenHouseRegistrationRow — one roster entry. Purely presentational over an
 * already-presented registration (openHousePresenter): the access token is
 * never rendered, only a lock hint when it is redacted. Emits the chosen
 * per-registration action so the host owns the API command.
 */
defineProps({
  registration: { type: Object, required: true },
  actionInFlight: { type: String, default: '' },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const actionLabel = action => {
  if (action === 'check_in') {
    return t('VISITS.OPEN_HOUSE.REGISTRATION.CHECK_IN');
  }
  if (action === 'convert') return t('VISITS.OPEN_HOUSE.REGISTRATION.CONVERT');
  if (action === 'no_show') return t('VISITS.OPEN_HOUSE.REGISTRATION.NO_SHOW');
  if (action === 'cancel') return t('VISITS.OPEN_HOUSE.REGISTRATION.CANCEL');
  return t('VISITS.OPEN_HOUSE.REGISTRATION.CONFIRM');
};

const statusLabel = status => {
  if (status === 'confirmed') {
    return t('VISITS.OPEN_HOUSE.REGISTRATION.STATUS.CONFIRMED');
  }
  if (status === 'waitlisted') {
    return t('VISITS.OPEN_HOUSE.REGISTRATION.STATUS.WAITLISTED');
  }
  if (status === 'promoted') {
    return t('VISITS.OPEN_HOUSE.REGISTRATION.STATUS.PROMOTED');
  }
  if (status === 'checked_in') {
    return t('VISITS.OPEN_HOUSE.REGISTRATION.STATUS.CHECKED_IN');
  }
  if (status === 'no_show') {
    return t('VISITS.OPEN_HOUSE.REGISTRATION.STATUS.NO_SHOW');
  }
  if (status === 'canceled') {
    return t('VISITS.OPEN_HOUSE.REGISTRATION.STATUS.CANCELED');
  }
  return t('VISITS.OPEN_HOUSE.REGISTRATION.STATUS.REGISTERED');
};
</script>

<template>
  <li
    class="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-start sm:justify-between"
  >
    <div class="flex min-w-0 flex-col gap-1.5">
      <div class="flex min-w-0 flex-wrap items-center gap-2">
        <LaisStatusBadge
          :label="statusLabel(registration.status)"
          :tone="registration.statusTone"
          size="sm"
        />
        <span class="truncate text-lais-body-sm font-semibold text-lais-text">
          {{ registration.contact.displayName }}
        </span>
        <span
          v-if="registration.accessTokenRedacted"
          class="i-lucide-lock size-3.5 shrink-0 text-lais-text-muted"
          :title="t('VISITS.OPEN_HOUSE.REGISTRATION.TOKEN_PROTECTED')"
          aria-hidden="true"
        />
        <span
          v-else-if="registration.accessToken"
          class="rounded-lais-sm bg-lais-bg-soft px-2 py-0.5 font-mono text-lais-label-xs text-lais-text-body"
        >
          {{ registration.accessToken }}
        </span>
      </div>
      <div
        class="flex flex-wrap gap-x-3 gap-y-1 text-lais-label-sm text-lais-text-muted"
      >
        <span>
          {{
            t('VISITS.OPEN_HOUSE.REGISTRATION.PARTY', {
              size: registration.partySize,
            })
          }}
        </span>
        <span v-if="registration.contact.phoneNumber">
          {{ registration.contact.phoneNumber }}
        </span>
        <span v-if="registration.contact.email">
          {{ registration.contact.email }}
        </span>
        <span v-if="registration.checkin?.checked_in_at">
          {{
            t('VISITS.OPEN_HOUSE.REGISTRATION.CHECKED_AT', {
              date: new Date(registration.checkin.checked_in_at).toLocaleString(
                'pt-BR',
                { timeZone: 'UTC' }
              ),
            })
          }}
        </span>
      </div>
      <div
        v-if="registration.conversions.length"
        class="flex flex-wrap gap-1.5"
      >
        <span
          v-for="conversion in registration.conversions"
          :key="`${registration.id}:${conversion.into}`"
          class="rounded-lais-sm bg-lais-primary-50 px-2 py-0.5 text-lais-label-xs font-semibold text-lais-primary"
        >
          {{
            t('VISITS.OPEN_HOUSE.REGISTRATION.CONVERTED_TO', {
              target: conversion.label,
            })
          }}
        </span>
      </div>
    </div>
    <div v-if="registration.actions.length" class="flex flex-wrap gap-1.5">
      <button
        v-for="action in registration.actions"
        :key="action"
        type="button"
        :disabled="Boolean(actionInFlight)"
        class="inline-flex min-h-8 items-center gap-1 rounded-lais-sm border border-lais-border-soft bg-lais-bg-card px-2.5 py-1 text-lais-label-sm font-semibold text-lais-text-body transition-colors hover:border-lais-primary/50 hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus disabled:opacity-50"
        @click="emit('action', { action, registration })"
      >
        {{ actionLabel(action) }}
      </button>
    </div>
  </li>
</template>
